import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
} from "@mui/material";
import {
  Add as AddIcon,
  Clear as ClearIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  FilterList as FilterIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import appointmentService from "../services/appointmentService";
import tenantService from "../services/tenantService";
import serviceService from "../services/serviceService";
import { darkColors, lightColors } from "../theme";
import { useTheme as useAppTheme } from "../contexts/ThemeContext";
import { createCommonStyles } from "../theme/styles";
import {
  colors,
  getTableHeaderStyles,
  getActualTableRowBackground,
} from "../theme/colors";
import Pagination from "../components/Pagination";
import AppointmentViewDialog from "../components/AppointmentViewDialog";

const Appointments = () => {
  const { user: currentUser } = useSelector((state) => state.auth);
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const { isDarkMode } = useAppTheme();
  const customColors = isDarkMode ? darkColors : lightColors;
  const commonStyles = createCommonStyles(isDarkMode);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [appointments, setAppointments] = useState([]);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [services, setServices] = useState([]);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, appointmentId: null });
  const [viewDialog, setViewDialog] = useState({ open: false, appointment: null });
  const [selectedTenantId, setSelectedTenantId] = useState("");
  // Helper funkcija za dobijanje sutrašnjeg datuma u formatu YYYY-MM-DD
  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const year = tomorrow.getFullYear();
    const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
    const day = String(tomorrow.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [viewMode, setViewMode] = useState("table"); // 'cards' ili 'table'
  const [statusFilter, setStatusFilter] = useState("all"); // 'all', 'pending', 'confirmed', 'cancelled'
  const [dateFrom, setDateFrom] = useState(getTomorrowDate());
  const [dateTo, setDateTo] = useState("");
  const [clientName, setClientName] = useState("");
  const [sortBy, setSortBy] = useState("dateAsc"); // 'date', 'dateDesc', 'createdAtDesc', 'customer', 'price', 'status'
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState({
    statusFilter: "all",
    dateFrom: getTomorrowDate(),
    dateTo: "",
    clientName: "",
    sortBy: "dateAsc",
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [userType, setUserType] = useState(currentUser?.userType || "customer");
  // Helper funkcija za dobijanje tenantId iz tenantConnections
  const getCurrentTenantId = useCallback(() => {
    // Ako postoji tenantId direktno na user objektu (stari način)
    if (currentUser?.tenantId) {
      return currentUser.tenantId?._id || currentUser.tenantId;
    }
    
    // Ako postoji tenantConnections, uzmi tenantId iz prve konekcije
    if (currentUser?.tenantConnections && currentUser.tenantConnections.length > 0) {
      const firstConnection = currentUser.tenantConnections[0];
      return firstConnection?.tenantId?._id || firstConnection?.tenantId;
    }
    
    return null;
  }, [currentUser]);
  
  // Helper funkcija za čitanje userType iz tenantConnections na osnovu selectedTenantId
  const getUserTypeFromCurrentTenant = useCallback(() => {
    if (!currentUser?.tenantConnections || currentUser.tenantConnections.length === 0) {
      return currentUser?.userType;
    }
    
    // Ako postoji selectedTenantId, traži taj connection
    if (selectedTenantId) {
      const connection = currentUser.tenantConnections.find(
        tc => {
          const tcTenantId = tc.tenantId?._id || tc.tenantId;
          return tcTenantId === selectedTenantId;
        }
      );
      
      if (connection?.userType) {
        return connection.userType;
      }
    }
    
    // Falls back na prvi connection ili user.userType
    const firstConnection = currentUser.tenantConnections[0];
    return firstConnection?.userType || currentUser?.userType;
  }, [currentUser, selectedTenantId]);
  // Infinite scroll state za karticni prikaz
  const [hasMoreCards, setHasMoreCards] = useState(true);
  const [loadingMoreCards, setLoadingMoreCards] = useState(false);
  const [currentPageCards, setCurrentPageCards] = useState(1);
  const [itemsPerPageCards] = useState(20); // Default 12 za karticni prikaz
  const [itemsPerPageMobile] = useState(5); // Default 5 za mobilni prikaz



  // === Stable fetchers (fixes exhaustive-deps) ===
  const fetchAppointments = useCallback(async () => {
    try {
      setLoadingAppointments(true);
      // Koristi helper funkciju za određivanje userType-a
      const actualUserType = getUserTypeFromCurrentTenant();
      const isSuperAdmin = actualUserType === "super_admin";
      const isTenantAdmin = actualUserType === "tenant_admin";
      const isCustomer = !actualUserType || actualUserType === "customer";
      const isEmployee = actualUserType === "employee";
      const tenantId = getCurrentTenantId();

      setUserType(actualUserType || "customer");
      // Za karticni prikaz ili mobilni koristi infinite scroll
      const pageToUse = viewMode === "cards" || isMobile ? 1 : page + 1;
      const limitToUse =
        viewMode === "cards"
          ? itemsPerPageCards
          : isMobile
          ? itemsPerPageMobile
          : rowsPerPage;

      // Parsiraj sortBy da ekstrahujem field i order
      let sortField = "createdAt";
      let sortOrder = "desc";
      
      if (sortBy) {
        if (sortBy === "createdAtDesc") {
          sortField = "createdAt";
          sortOrder = "desc";
        } else if (sortBy === "dateAsc") {
          sortField = "appointmentDate";
          sortOrder = "asc";
        } else if (sortBy === "dateDesc") {
          sortField = "appointmentDate";
          sortOrder = "desc";
        } else if (sortBy === "priceAsc") {
          sortField = "totalPrice";
          sortOrder = "asc";
        } else if (sortBy === "priceDesc") {
          sortField = "totalPrice";
          sortOrder = "desc";
        }
      }

      // Pripremi filtere za backend
      const filters = {
        status: statusFilter !== "all" ? statusFilter : undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        clientName: clientName.trim() || undefined,
        sortBy: sortField,
        sortOrder: sortOrder,
      };

      // Ukloni undefined vrednosti
      Object.keys(filters).forEach(key => filters[key] === undefined && delete filters[key]);

      let response;
      if (isSuperAdmin) {
        response = await appointmentService.getAllAppointments(
          pageToUse,
          limitToUse,
          filters
        );
      } else if (isCustomer) {
        // Za obične korisnike (customer ili one bez userType) - prikaži samo njihove termine
        // Ako je postavljen tenantId, filtriraj termine samo za tog tenanta
        const customerTenantId = tenantId;
        response = await appointmentService.getAppointmentsByCustomer(
          currentUser._id,
          pageToUse,
          limitToUse,
          customerTenantId,
          filters
        );
      } else if (isEmployee) {
        // Za zaposlene (employee) - prikaži termine gde su oni zaposleni
        response = await appointmentService.getAppointmentsByEmployee(
          currentUser._id,
          pageToUse,
          limitToUse,
          filters
        );
      } else if (isTenantAdmin && tenantId) {
        response = await appointmentService.getAppointmentsByTenant(
          tenantId,
          pageToUse,
          limitToUse,
          filters
        );
      } else {
        setAppointments([]);
        setTotalCount(0);
        return;
      }

      // Backend vraća podatke direktno (nije u response.data)
      const appointmentsToSet = response.appointments || response;
      // Postavi samo ako je validan array, zadrži stare podatke dok se učitavaju novi
      if (Array.isArray(appointmentsToSet)) {
        setAppointments(appointmentsToSet);
      }

      // Postavi total count
      const total =
        parseInt(response.total) ||
        (response.appointments ? response.appointments.length : 0);
      setTotalCount(total);

      // Za karticni prikaz ili mobilni postavi infinite scroll state
      if (viewMode === "cards" || isMobile) {
        setCurrentPageCards(1);
        setHasMoreCards(appointmentsToSet.length < total);
      } else {
        // Sinhronizuj page sa backend-om za tabelu
        const backendPage = parseInt(response.currentPage) - 1;
        if (backendPage !== page && Math.abs(backendPage - page) > 0) {
          setPage(backendPage);
        }
      }
    } catch (err) {
      console.error("fetchAppointments - error:", err);
      toast.error(err.message);
    } finally {
      setLoadingAppointments(false);
    }
  }, [
    currentUser,
    page,
    rowsPerPage,
    viewMode,
    itemsPerPageCards,
    itemsPerPageMobile,
    isMobile,
    getCurrentTenantId,
    getUserTypeFromCurrentTenant,
    statusFilter,
    dateFrom,
    dateTo,
    clientName,
    sortBy,
  ]);

  // Infinite scroll funkcija za karticni prikaz
  const loadMoreAppointments = useCallback(async () => {
    if (loadingMoreCards || !hasMoreCards) return;

    try {
      setLoadingMoreCards(true);
      const nextPage = currentPageCards + 1;

      // Koristi helper funkciju za određivanje userType-a
      const actualUserType = getUserTypeFromCurrentTenant();
      const isSuperAdmin = actualUserType === "super_admin";
      const isTenantAdmin = actualUserType === "tenant_admin";
      const isCustomer = !actualUserType || actualUserType === "customer";
      const isEmployee = actualUserType === "employee";
      const tenantId = getCurrentTenantId();

      const limitToUse =
        viewMode === "cards" ? itemsPerPageCards : itemsPerPageMobile;

      // Parsiraj sortBy da ekstrahujem field i order
      let sortField = "createdAt";
      let sortOrder = "desc";
      
      if (sortBy) {
        if (sortBy === "createdAtDesc") {
          sortField = "createdAt";
          sortOrder = "desc";
        } else if (sortBy === "dateAsc") {
          sortField = "appointmentDate";
          sortOrder = "asc";
        } else if (sortBy === "dateDesc") {
          sortField = "appointmentDate";
          sortOrder = "desc";
        } else if (sortBy === "priceAsc") {
          sortField = "totalPrice";
          sortOrder = "asc";
        } else if (sortBy === "priceDesc") {
          sortField = "totalPrice";
          sortOrder = "desc";
        }
      }

      // Pripremi filtere za backend
      const filters = {
        status: statusFilter !== "all" ? statusFilter : undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        clientName: clientName.trim() || undefined,
        sortBy: sortField,
        sortOrder: sortOrder,
      };

      // Ukloni undefined vrednosti
      Object.keys(filters).forEach(key => filters[key] === undefined && delete filters[key]);

      let response;
      if (isSuperAdmin) {
        response = await appointmentService.getAllAppointments(
          nextPage,
          limitToUse,
          filters
        );
      } else if (isCustomer) {
        const customerTenantId = tenantId;
        response = await appointmentService.getAppointmentsByCustomer(
          currentUser._id,
          nextPage,
          limitToUse,
          customerTenantId,
          filters
        );
      } else if (isEmployee) {
        response = await appointmentService.getAppointmentsByEmployee(
          currentUser._id,
          nextPage,
          limitToUse,
          filters
        );
      } else if (isTenantAdmin && tenantId) {
        response = await appointmentService.getAppointmentsByTenant(
          tenantId,
          nextPage,
          limitToUse,
          filters
        );
      } else {
        return;
      }

      const appointmentsToSet = response.appointments || response;

      if (Array.isArray(appointmentsToSet) && appointmentsToSet.length > 0) {
        setAppointments((prev) => [...prev, ...appointmentsToSet]);
        setCurrentPageCards(nextPage);

        // Proveri da li ima još podataka
        const total = parseInt(response.total) || 0;
        const loadedCount = appointments.length + appointmentsToSet.length;
        setHasMoreCards(loadedCount < total);
      } else {
        setHasMoreCards(false);
      }
    } catch (err) {
      console.error("Error loading more appointments:", err);
    } finally {
      setLoadingMoreCards(false);
    }
  }, [
    currentUser,
    currentPageCards,
    itemsPerPageCards,
    itemsPerPageMobile,
    viewMode,
    loadingMoreCards,
    hasMoreCards,
    appointments.length,
    getCurrentTenantId,
    getUserTypeFromCurrentTenant,
    statusFilter,
    dateFrom,
    dateTo,
    clientName,
    sortBy,
  ]);

  // Scroll event listener za infinite scroll u karticnom prikazu
  useEffect(() => {
    if (viewMode !== "cards" && !isMobile) return;

    const handleScroll = () => {
      const scrollTop = document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = window.innerHeight;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 200;

      if (isNearBottom) {
        loadMoreAppointments();
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [viewMode, loadMoreAppointments, isMobile]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  console.log('userType u fetchAppointments:', userType);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };


  const fetchTenants = useCallback(async () => {
    try {
      // Koristi helper funkciju za određivanje userType-a
      const actualUserType = getUserTypeFromCurrentTenant();
      const isSuperAdmin = actualUserType === "super_admin";
      const isTenantAdmin = actualUserType === "tenant_admin";
      const isCustomer = !actualUserType || actualUserType === "customer";
      const tenantId = getCurrentTenantId();

      if (isSuperAdmin) {
        const response = await tenantService.getAllTenants();
        setTenants(response.tenants || response);
      } else if ((isTenantAdmin || isCustomer) && tenantId) {
        // Za tenant_admin ili korisnike bez userType (customer), učitaj puni tenant objekat sa settings
        const response = await tenantService.getTenantById(tenantId);
        setTenants([response]);
      } else if (tenantId) {
        // Ako ima tenantId ali nije super_admin ili tenant_admin, pokušaj da učitaš tenant
        const response = await tenantService.getTenantById(tenantId);
        setTenants([response]);
      } else {
        setTenants([]);
      }
    } catch (err) {
      console.error("Error fetching tenants:", err);
    }
  }, [getCurrentTenantId, getUserTypeFromCurrentTenant]);

  const fetchServices = useCallback(
    async (tenantIdOverride = null) => {
      try {
        // Koristi helper funkciju za određivanje userType-a
        const actualUserType = getUserTypeFromCurrentTenant();
        const isSuperAdmin = actualUserType === "super_admin";
        const isTenantAdmin = actualUserType === "tenant_admin";
        const tenantId =
          tenantIdOverride ||
          getCurrentTenantId();

        let response;
        if (isSuperAdmin && tenantId) {
          response = await serviceService.getServicesByTenant(
            tenantId,
            1,
            9999
          ); // Uzmi sve usluge
        } else if ((isTenantAdmin && tenantId) || tenantId) {
          response = await serviceService.getServicesByTenant(
            tenantId,
            1,
            9999
          ); // Uzmi sve usluge
        } else {
          setServices([]);
          return;
        }

        const servicesData = response.services || response;
        setServices(servicesData);
      } catch (err) {
        console.error("Error fetching services:", err);
      }
    },
    [getCurrentTenantId, getUserTypeFromCurrentTenant]
  );


  useEffect(() => {
    // dependencies are the stable callbacks
    fetchAppointments();
    fetchTenants();
    fetchServices();
  }, [fetchAppointments, fetchTenants, fetchServices]);

  // Resetuj page na 0 i infinite scroll state kada se filteri ili sortBy promene
  // Nemoj resetovati appointments ovde, fetchAppointments će se pozvati automatski kroz dependencies
  useEffect(() => {
    setPage(0);
    setCurrentPageCards(1);
    setHasMoreCards(true);
  }, [statusFilter, dateFrom, dateTo, clientName, sortBy]);

  // Handle navigation state from Test page or Homepage

  const handleOpenDialog = useCallback(
    (appointment = null) => {
      // Navigate to booking page instead of opening modal
      if (appointment) {
        navigate(`/appointment-booking/${appointment._id}`);
      } else {
        navigate('/appointment-booking/new');
      }
    },
    [navigate]
  );

  useEffect(() => {
    if (location.state) {
      const { selectedTenantId, openCreateModal, openNewAppointment } =
        location.state;

      if (selectedTenantId) {
        setSelectedTenantId(selectedTenantId);

        if (openCreateModal || openNewAppointment) {
          // Delay opening modal to allow state to update
          setTimeout(() => {
            handleOpenDialog();
          }, 500);
        }
      } else if (openNewAppointment) {
        // Ako je samo openNewAppointment bez selectedTenantId (korisnik ima setovan tenantId)
        setTimeout(() => {
          handleOpenDialog();
        }, 500);
      }

      // Clear navigation state to prevent re-triggering
      window.history.replaceState({}, document.title);
    }
  }, [location.state, handleOpenDialog]);

  // Postavi selectedTenantId za tenant_admin i customer korisnike i učitaj kategorije
  useEffect(() => {
    const actualUserType = getUserTypeFromCurrentTenant();
    if (actualUserType === "super_admin") {
      // selectedTenantId će biti postavljen od strane korisnika
      // Ne učitavaj kategorije dok se ne selektuje tenant
    } else {
      // Koristi helper funkciju za dobijanje tenantId
      const tenantId = getCurrentTenantId();

      if (tenantId) {
        setSelectedTenantId(tenantId);
      } else {
        console.warn('⚠️ Appointments - No tenantId found for user:', currentUser);
      }
    }
  }, [currentUser, getUserTypeFromCurrentTenant, getCurrentTenantId]);

  // Učitaj usluge kada se promeni selectedTenantId
  useEffect(() => {
    if (selectedTenantId) {
      fetchServices(selectedTenantId);
    } else {
      // Proveri da li je super_admin bez selektovanog tenanta
      const actualUserType = getUserTypeFromCurrentTenant();
      if (actualUserType === "super_admin" && !selectedTenantId) {
        // Resetuj usluge kada nije izabran tenant
        setServices([]);
      }
    }
  }, [selectedTenantId, getUserTypeFromCurrentTenant, fetchServices]);


  // Event listener za dugme iz header-a
  useEffect(() => {
    const handleHeaderOpenDialog = () => {
      handleOpenDialog();
    };

    window.addEventListener("openAppointmentDialog", handleHeaderOpenDialog);
    return () => {
      window.removeEventListener(
        "openAppointmentDialog",
        handleHeaderOpenDialog
      );
    };
  }, [handleOpenDialog]);

  // Mobilni filter funkcije
  const handleOpenMobileFilter = () => {
    setTempFilters({
      statusFilter,
      dateFrom,
      dateTo,
      clientName,
      sortBy,
    });
    setMobileFilterOpen(true);
  };

  const handleCloseMobileFilter = () => {
    setMobileFilterOpen(false);
  };

  const handleApplyMobileFilters = () => {
    setStatusFilter(tempFilters.statusFilter);
    setDateFrom(tempFilters.dateFrom);
    setDateTo(tempFilters.dateTo);
    setClientName(tempFilters.clientName);
    setSortBy(tempFilters.sortBy);
    setMobileFilterOpen(false);
  };

  const handleResetMobileFilters = () => {
    setTempFilters({
      statusFilter: "all",
      dateFrom: getTomorrowDate(),
      dateTo: "",
      clientName: "",
      sortBy: "dateAsc",
    });
  };

  const handleRemoveActiveFilter = (filterType) => {
    switch (filterType) {
      case "status":
        setStatusFilter("all");
        break;
      case "dateFrom":
        setDateFrom("");
        break;
      case "dateTo":
        setDateTo("");
        break;
      case "client":
        setClientName("");
        break;
      case "sort":
        setSortBy("createdAtDesc");
        break;
      default:
        break;
    }
  };


  // Funkcija za brzu promenu statusa
  const handleQuickStatusChange = async (appointmentId, newStatus) => {
    try {
      const isCustomer = !currentUser?.userType || currentUser?.userType === "customer";
      // Za customer korisnike, proveri da li je otkazivanje dozvoljeno (do dana pre termina)
      if (isCustomer && newStatus === "cancelled") {
        const appointment = appointments.find(a => a._id === appointmentId);
        if (appointment) {
          const appointmentDate = new Date(appointment.appointmentDate);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          appointmentDate.setHours(0, 0, 0, 0);
          
          // Proveri da li je danasšnji datum manji od datuma termina
          if (today >= appointmentDate) {
            toast.error("Ne možete otkazati termin danas ili nakon datuma termina");
            return;
          }
        }
      }

      // Samo customer korisnici imaju ograničenja (mogu samo da otkazuju)
      // Super admin, tenant admin i employee mogu da menjaju statuse bez ograničenja
      // Nema provere dozvola za tenant_admin i employee

      // Koristi specifične metode za potvrđivanje, otkazivanje i postavljanje na čekanju
      if (newStatus === "confirmed") {
        await appointmentService.confirmAppointment(appointmentId);
        toast.success("Termin je potvrđen!");
      } else if (newStatus === "cancelled") {
        await appointmentService.cancelAppointment(
          appointmentId,
          "Status promenjen od strane admin-a"
        );
        toast.success("Termin je otkazan!");
      } else if (newStatus === "pending") {
        await appointmentService.setAppointmentPending(appointmentId);
        toast.success("Termin je postavljen na čekanju!");
      } else {
        // Za ostale statuse koristi generičku metodu
        await appointmentService.updateAppointment(appointmentId, {
          status: newStatus,
        });
        toast.success("Status termina je ažuriran!");
      }

      // Ponovno učitaj podatke
      await fetchAppointments();
    } catch (err) {
      console.error("Error updating appointment status:", err);

      // Prikaži specifičnu grešku iz backend-a
      let errorMessage = "Greška pri ažuriranju statusa termina";

      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }

      toast.error(errorMessage);
    }
  };

  // Funkcija za otvaranje dialoga za brisanje
  const handleDeleteClick = (appointmentId) => {
    setDeleteDialog({ open: true, appointmentId });
  };

  // Funkcija za zatvaranje dialoga za brisanje
  const handleDeleteDialogClose = () => {
    setDeleteDialog({ open: false, appointmentId: null });
  };

  // Funkcija za potvrdu brisanja
  const handleConfirmDelete = async () => {
    if (!deleteDialog.appointmentId) return;

    try {
      await appointmentService.deleteAppointment(deleteDialog.appointmentId);
      toast.success("Termin je uspešno obrisan!");
      await fetchAppointments();
      handleDeleteDialogClose();
    } catch (err) {
      console.error("Error deleting appointment:", err);
      let errorMessage = "Greška pri brisanju termina";
      
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      toast.error(errorMessage);
    }
  };

  // Funkcija za otvaranje modala za pregled termina
  const handleViewClick = (appointment) => {
    setViewDialog({ open: true, appointment });
  };

  // Funkcija za zatvaranje modala za pregled termina
  const handleViewDialogClose = () => {
    setViewDialog({ open: false, appointment: null });
  };

  // Backend sada filtrira i sortira, koristimo direktno appointments
  // Memoize da se ne re-renderuje osim ako se appointments promene
  const filteredAndSortedAppointments = useMemo(() => appointments, [appointments]);

  return (
    <div>
      {/* Filter i Sort Controls */}
      {isMobile ? (
        <Box sx={{ width: "100%" }}>
          {/* Mobilni pristup - dugmad za filtere */}
          <Box
            sx={{
              display: "flex",
              gap: 1,
              mb: 2,
              width: "100%",
            }}
          >
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={handleOpenMobileFilter}
              sx={{
                flex: 1,
                borderColor: customColors.border.light,
                color: commonStyles.textPrimary.color,
                "&:hover": {
                  borderColor: customColors.primary.main,
                  backgroundColor: "rgba(59, 130, 246, 0.1)",
                },
              }}
            >
              Filteri
            </Button>
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel sx={{ color: commonStyles.textSecondary.color }}>
                Sortiraj
              </InputLabel>
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                sx={{
                  color: commonStyles.textPrimary.color,
                  background: getTableHeaderStyles(isDarkMode).cellBackground,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: customColors.border.light,
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: customColors.primary.main,
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: customColors.primary.main,
                  },
                  "& .MuiSelect-icon": {
                    color: commonStyles.textPrimary.color,
                    background: getTableHeaderStyles(isDarkMode).cellBackground,
                  },
                }}
              >
                <MenuItem value="createdAtDesc">Datum kreiranja (najnoviji)</MenuItem>
                <MenuItem value="dateAsc">Datum rastuće</MenuItem>
                <MenuItem value="dateDesc">Datum opadajuće</MenuItem>
                <MenuItem value="priceAsc">Cena rastuće</MenuItem>
                <MenuItem value="priceDesc">Cena opadajuće</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Aktivni filteri - u novom redu ispod dugmića */}
          {(statusFilter !== "all" || dateFrom || dateTo) && (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                mb: 2,
                justifyContent: "center",
                width: "100%",
              }}
            >
              {statusFilter !== "all" && (
                <Chip
                  label={`Status: ${
                    statusFilter === "pending"
                      ? "Na čekanju"
                      : statusFilter === "confirmed"
                      ? "Potvrđeno"
                      : statusFilter === "cancelled"
                      ? "Otkazano"
                      : statusFilter
                  }`}
                  onDelete={() => handleRemoveActiveFilter("status")}
                  deleteIcon={<CloseIcon />}
                  sx={{
                    backgroundColor: "transparent",
                    color: commonStyles.textPrimary.color,
                    background: getTableHeaderStyles(isDarkMode).cellBackground,
                    border: "none",
                    "& .MuiChip-deleteIcon": {
                      color: commonStyles.textSecondary.color,
                    },
                    "&:hover": {
                      backgroundColor: colors.ui.background.dark,
                    },
                  }}
                />
              )}
              {dateFrom && (
                <Chip
                  label={`Datum od: ${new Date(dateFrom).toLocaleDateString(
                    "sr-RS"
                  )}`}
                  onDelete={() => handleRemoveActiveFilter("dateFrom")}
                  deleteIcon={<CloseIcon />}
                  sx={{
                    backgroundColor: "transparent",
                    color: commonStyles.textPrimary.color,
                    background: getTableHeaderStyles(isDarkMode).cellBackground,
                    border: "none",
                    "& .MuiChip-deleteIcon": {
                      color: commonStyles.textSecondary.color,
                    },
                    "&:hover": {
                      backgroundColor: colors.ui.background.dark,
                    },
                  }}
                />
              )}
              {dateTo && (
                <Chip
                  label={`Datum do: ${new Date(dateTo).toLocaleDateString(
                    "sr-RS"
                  )}`}
                  onDelete={() => handleRemoveActiveFilter("dateTo")}
                  deleteIcon={<CloseIcon />}
                  sx={{
                    backgroundColor: "transparent",
                    color: commonStyles.textPrimary.color,
                    background: getTableHeaderStyles(isDarkMode).cellBackground,
                    border: "none",
                    "& .MuiChip-deleteIcon": {
                      color: commonStyles.textSecondary.color,
                    },
                    "&:hover": {
                      backgroundColor: colors.ui.background.dark,
                    },
                  }}
                />
              )}
            </Box>
          )}
        </Box>
      ) : (
        /* Desktop pristup - postojeći filteri */
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 2.5,
            alignItems: "flex-start",
            mb: 3,
            flexWrap: "wrap",
          }}
        >
          {/* Status Filter */}
          <Box sx={{ minWidth: 160 }}>
            <Typography
              variant="body2"
              sx={{
                color: commonStyles.textPrimary.color,
                fontWeight: 600,
                mb: 1,
                fontSize: "0.875rem",
              }}
            >
              Status
            </Typography>
            <FormControl size="small" fullWidth>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                displayEmpty
                sx={{
                  color: commonStyles.textPrimary.color,
                  background: getTableHeaderStyles(isDarkMode).cellBackground,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: customColors.border.light,
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: customColors.primary.main,
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: customColors.primary.main,
                  },
                  "& .MuiSelect-icon": {
                    color: commonStyles.textPrimary.color,
                    background: getTableHeaderStyles(isDarkMode).cellBackground,
                  },
                }}
              >
                <MenuItem value="all">Svi statusi</MenuItem>
                <MenuItem value="confirmed">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: colors.status.appointment.confirmed,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CheckCircleIcon sx={{ fontSize: 6, color: "white" }} />
                    </Box>
                    Potvrđeno
                  </Box>
                </MenuItem>
                <MenuItem value="cancelled">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: colors.status.appointment.cancelled,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CancelIcon sx={{ fontSize: 6, color: "white" }} />
                    </Box>
                    Otkazano
                  </Box>
                </MenuItem>
                <MenuItem value="pending">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: colors.status.appointment.pending,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <ScheduleIcon sx={{ fontSize: 6, color: "white" }} />
                    </Box>
                    Na čekanju
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Datum od */}
          <Box sx={{ minWidth: 180 }}>
            <Typography
              variant="body2"
              sx={{
                color: commonStyles.textPrimary.color,
                fontWeight: 600,
                mb: 1,
                fontSize: "0.875rem",
              }}
            >
              Datum od
            </Typography>
            <TextField
              size="small"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              fullWidth
              InputLabelProps={{
                shrink: false,
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  color: commonStyles.textPrimary.color,
                  background: getTableHeaderStyles(isDarkMode).cellBackground,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: customColors.border.light,
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: customColors.primary.main,
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: customColors.primary.main,
                  },
                },
              }}
            />
          </Box>

          {/* Datum do */}
          <Box sx={{ minWidth: 180 }}>
            <Typography
              variant="body2"
              sx={{
                color: commonStyles.textPrimary.color,
                fontWeight: 600,
                mb: 1,
                fontSize: "0.875rem",
              }}
            >
              Datum do
            </Typography>
            <TextField
              size="small"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              fullWidth
              InputLabelProps={{
                shrink: false,
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  color: commonStyles.textPrimary.color,
                  background: getTableHeaderStyles(isDarkMode).cellBackground,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: customColors.border.light,
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: customColors.primary.main,
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: customColors.primary.main,
                  },
                },
              }}
            />
          </Box>

          {/* Sortiranje */}
          <Box sx={{ minWidth: 220 }}>
            <Typography
              variant="body2"
              sx={{
                color: commonStyles.textPrimary.color,
                fontWeight: 600,
                mb: 1,
                fontSize: "0.875rem",
              }}
            >
              Sortiraj po
            </Typography>
            <FormControl size="small" fullWidth>
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                displayEmpty
                sx={{
                  color: commonStyles.textPrimary.color,
                  background: getTableHeaderStyles(isDarkMode).cellBackground,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: customColors.border.light,
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: customColors.primary.main,
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: customColors.primary.main,
                  },
                  "& .MuiSelect-icon": {
                    color: commonStyles.textPrimary.color,
                    background: getTableHeaderStyles(isDarkMode).cellBackground,
                  },
                }}
              >
                <MenuItem value="createdAtDesc">Datum kreiranja (najnoviji)</MenuItem>
                <MenuItem value="dateAsc">Datum rastuće</MenuItem>
                <MenuItem value="dateDesc">Datum opadajuće</MenuItem>
                <MenuItem value="priceAsc">Cena rastuće</MenuItem>
                <MenuItem value="priceDesc">Cena opadajuće</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Resetuj filtere ikonica */}
          <Box sx={{ display: "flex", alignItems: "flex-end", height: "56px", pb: "4px" }}>
            <IconButton
              onClick={() => {
                setStatusFilter("all");
                setDateFrom(getTomorrowDate());
                setDateTo("");
                setClientName("");
                setSortBy("dateAsc");
              }}
              sx={{
                color: commonStyles.textSecondary.color,
                border: `1px solid ${customColors.border.light}`,
                borderRadius: 1,
                "&:hover": {
                  color: customColors.primary.main,
                  borderColor: customColors.primary.main,
                  backgroundColor: "rgba(59, 130, 246, 0.1)",
                },
              }}
              title="Resetuj filtere"
            >
              <ClearIcon />
            </IconButton>
          </Box>
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          gap: 1,
          alignItems: "center",
          justifyContent: !isMobile ? "space-between" : "center",
        }}
      >
        {/* Desktop dugme i toggle */}
        {!isMobile && (
          <>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={commonStyles.gradientButton}
            >
              Novi termin
            </Button>

            <IconButton
              onClick={() =>
                setViewMode(viewMode === "cards" ? "table" : "cards")
              }
              sx={{
                ...commonStyles.iconButton,
                background:
                  viewMode === "cards"
                    ? "rgba(59, 130, 246, 0.1)"
                    : colors.ui.background.dark,
                border:
                  viewMode === "cards"
                    ? "1px solid rgba(59, 130, 246, 0.3)"
                    : `1px solid ${customColors.border.light}`,
                "&:hover": {
                  background:
                    viewMode === "cards"
                      ? "rgba(59, 130, 246, 0.2)"
                      : colors.ui.background.medium,
                },
              }}
              title={
                viewMode === "cards"
                  ? "Prebaci na tabelarni prikaz"
                  : "Prebaci na card prikaz"
              }
            >
              {viewMode === "cards" ? <ViewListIcon /> : <ViewModuleIcon />}
            </IconButton>
          </>
        )}
      </Box>

      {/* Conditional prikaz - kartice ili tabela */}
      {(filteredAndSortedAppointments.length > 0 || loadingAppointments) ? (
        isMobile || viewMode === "cards" ? (
          /* Card prikaz */
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: isMobile
              ? "1fr"
              : "repeat(auto-fill, minmax(400px, 1fr))",
            gap: 3,
            p: 2,
            background: isDarkMode
              ? "rgba(0, 0, 0, 0.1)"
              : "rgba(240, 240, 240, 0.5)",
            borderRadius: 2,
            position: "relative",
            opacity: loadingAppointments ? 0.5 : 1,
            transition: "opacity 0.2s ease",
            minHeight: "400px",
          }}
        >
          {loadingAppointments && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 4,
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 10,
                backgroundColor: isDarkMode
                  ? "rgba(26, 26, 46, 0.9)"
                  : "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(2px)",
              }}
            >
              <CircularProgress />
            </Box>
          )}
          {filteredAndSortedAppointments.map((appointment, index) => (
              <Card
                key={appointment._id}
                sx={{
                  background: isDarkMode
                    ? "rgba(30, 41, 59, 0.8)"
                    : "rgba(255, 255, 255, 1)",
                  border: isDarkMode
                    ? "1px solid rgba(148, 163, 184, 0.1)"
                    : "1px solid rgba(0, 0, 0, 0.1)",
                  borderRadius: 2,
                  transition: "all 0.2s ease",
                  boxShadow: isDarkMode
                    ? "0 4px 12px rgba(0, 0, 0, 0.3)"
                    : "0 4px 12px rgba(0, 0, 0, 0.1)",
                  "&:hover": {
                    background: isDarkMode
                      ? "rgba(30, 41, 59, 0.9)"
                      : "rgba(255, 255, 255, 1)",
                    border: isDarkMode
                      ? "1px solid rgba(148, 163, 184, 0.2)"
                      : "1px solid rgba(0, 0, 0, 0.2)",
                    boxShadow: isDarkMode
                      ? "0 8px 20px rgba(0, 0, 0, 0.4)"
                      : "0 8px 20px rgba(0, 0, 0, 0.15)",
                  },
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  {/* Header sa naslovom, datumom/vremenom i akcijama */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      mb: 2,
                      gap: 2,
                    }}
                  >
                    {/* Naslov */}
                    <Typography
                      variant="h6"
                      sx={{
                        ...commonStyles.textPrimary,
                        fontWeight: 600,
                        flex: 1,
                      }}
                    >
                      {appointment.isQuickAppointment &&
                      appointment.customerName
                        ? appointment.customerName
                        : appointment.customerId
                        ? typeof appointment.customerId === "object"
                          ? `${appointment.customerId.firstName || ""} ${
                              appointment.customerId.lastName || ""
                            }`.trim()
                          : "N/A"
                        : "N/A"}
                    </Typography>


                    {/* Datum i vreme - desna strana */}
                    <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          ...commonStyles.textSecondary,
                          fontSize: "0.875rem",
                          fontWeight: 500,
                        }}
                      >
                        {new Date(
                          appointment.appointmentDate
                        ).toLocaleDateString("sr-RS")}
                        {appointment.selectedTime && `, ${appointment.selectedTime}`}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Usluge */}
                  <Typography
                    variant="body2"
                    sx={{
                      ...commonStyles.textSecondary,
                      mb: 1,
                    }}
                  >
                    {appointment.serviceIds && appointment.serviceIds.length > 0
                      ? appointment.serviceIds
                          .map((service) =>
                            typeof service === "object" ? service.name : service
                          )
                          .join(", ")
                      : "Nema usluga"}
                  </Typography>

                  {/* Cena i Trajanje */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        ...commonStyles.textPrimary,
                        fontWeight: 600,
                        fontSize: "1rem",
                      }}
                    >
                      Cena:{" "}
                      {appointment.totalPrice ||
                        appointment.calculatedTotalPrice ||
                        "N/A"}{" "}
                      RSD
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        ...commonStyles.textSecondary,
                        fontSize: "0.9rem",
                      }}
                    >
                      Trajanje:{" "}
                      {appointment.duration || appointment.totalDuration || 0}{" "}
                      min
                    </Typography>
                  </Box>

                  {/* Status dropdown i akcije u istom redu */}
                  <Box
                    sx={{
                      mt: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                    }}
                  >

                    {/* Status dropdown i akcije */}
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center", justifyContent: "space-between" }}>
                      {/* Status dropdown - za sve korisnike */}
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select
                          value={appointment.status}
                          onChange={(e) =>
                            handleQuickStatusChange(
                              appointment._id,
                              e.target.value
                            )
                          }
                          MenuProps={{
                            PaperProps: {
                              sx: commonStyles.selectMenu,
                            },
                          }}
                          sx={{
                            fontSize: "0.75rem",
                            height: "32px",
                            background:
                              getTableHeaderStyles(isDarkMode).cellBackground,
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: isDarkMode
                                ? "rgba(255, 255, 255, 0.2)"
                                : "rgba(0, 0, 0, 0.2)",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: customColors.primary.main,
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: customColors.primary.main,
                            },
                            "& .MuiSelect-select": {
                              padding: "6px 8px",
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                            },
                          }}
                          renderValue={(value) => (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              {value === "pending" && (
                                <ScheduleIcon
                                  sx={{
                                    fontSize: 14,
                                    color: "#f59e0b !important",
                                  }}
                                />
                              )}
                              {value === "confirmed" && (
                                <CheckCircleIcon
                                  sx={{
                                    fontSize: 14,
                                    color: "#10b981 !important",
                                  }}
                                />
                              )}
                              {value === "cancelled" && (
                                <CancelIcon
                                  sx={{
                                    fontSize: 14,
                                    color: "#ef4444 !important",
                                  }}
                                />
                              )}
                              <Typography sx={{ fontSize: "0.7rem" }}>
                                {value === "pending"
                                  ? "Na čekanju"
                                  : value === "confirmed"
                                  ? "Potvrđeno"
                                  : value === "cancelled"
                                  ? "Otkazano"
                                  : value}
                              </Typography>
                            </Box>
                          )}
                        >
                          <MenuItem
                            value="pending"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              background:
                                getTableHeaderStyles(isDarkMode).cellBackground,
                              color: isDarkMode
                                ? "rgba(255, 255, 255, 0.9)"
                                : "rgba(0, 0, 0, 0.9)",
                              "&:hover": {
                                background: isDarkMode
                                  ? "rgba(255, 255, 255, 0.1)"
                                  : "rgba(0, 0, 0, 0.1)",
                              },
                            }}
                          >
                            <ScheduleIcon
                              sx={{ fontSize: 16, color: "#ffc107" }}
                            />
                            Na čekanju
                          </MenuItem>
                       { userType !== 'customer' && (  
                        <MenuItem
                            value="confirmed"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              background:
                                getTableHeaderStyles(isDarkMode).cellBackground,
                              color: isDarkMode
                                ? "rgba(255, 255, 255, 0.9)"
                                : "rgba(0, 0, 0, 0.9)",
                              "&:hover": {
                                background: isDarkMode
                                  ? "rgba(255, 255, 255, 0.1)"
                                  : "rgba(0, 0, 0, 0.1)",
                              },
                            }}
                          >
                            <CheckCircleIcon
                              sx={{ fontSize: 16, color: "#28a745" }}
                            />
                            Potvrđeno
                          </MenuItem>
                          )}
                          <MenuItem
                            value="cancelled"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              background:
                                getTableHeaderStyles(isDarkMode).cellBackground,
                              color: isDarkMode
                                ? "rgba(255, 255, 255, 0.9)"
                                : "rgba(0, 0, 0, 0.9)",
                              "&:hover": {
                                background: isDarkMode
                                  ? "rgba(255, 255, 255, 0.1)"
                                  : "rgba(0, 0, 0, 0.1)",
                              },
                            }}
                          >
                            <CancelIcon
                              sx={{ fontSize: 16, color: "#dc3545" }}
                            />
                            Otkazano
                          </MenuItem>
                        </Select>
                      </FormControl>

                      {/* Akcije - skroz desno */}
                      <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", ml: "auto" }}>
                        {/* View dugme - za sve korisnike */}
                        <IconButton
                          size="small"
                          onClick={() => handleViewClick(appointment)}
                          sx={{
                            color: colors.actions.view,
                            transition: "all 0.2s ease",
                            "&:hover": {
                              backgroundColor: `${colors.actions.view}20`,
                              transform: "scale(1.1)",
                            },
                          }}
                          title="Pregled termina"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>

                        {/* Edit dugme - samo za pending status */}
                        {appointment.status === "pending" && (
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(appointment)}
                            sx={{
                              color: customColors.primary.main,
                              transition: "all 0.2s ease",
                              "&:hover": {
                                backgroundColor: `${customColors.primary.main}20`,
                                transform: "scale(1.1)",
                              },
                            }}
                            title="Izmeni termin"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        )}

                        {/* Delete dugme - samo za cancelled status */}
                        {appointment.status === "cancelled" && (
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(appointment._id)}
                            sx={{
                              color: colors.actions.delete,
                              transition: "all 0.2s ease",
                              "&:hover": {
                                backgroundColor: `${colors.actions.delete}20`,
                                transform: "scale(1.1)",
                              },
                            }}
                            title="Obriši termin"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </Box>

                  </Box>
                </CardContent>
              </Card>
            ))}

            {/* Loading indicator za infinite scroll u karticnom prikazu */}
            {loadingMoreCards && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  py: 3,
                }}
              >
                <CircularProgress size={24} sx={{ mr: 2 }} />
                <Typography variant="body2" sx={commonStyles.textSecondary}>
                  Učitavanje dodatnih termina...
                </Typography>
              </Box>
            )}

            {/* End of list indicator za karticni prikaz */}
            {!hasMoreCards && filteredAndSortedAppointments.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  py: 2,
                }}
              >
                <Typography variant="body2" sx={commonStyles.textSecondary}>
                  Prikazano je svih {filteredAndSortedAppointments.length}{" "}
                  termina
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          /* Table prikaz */
          <div style={{ position: "relative" }}>
            {loadingAppointments && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  py: 4,
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  zIndex: 10,
                  backgroundColor: isDarkMode
                    ? "rgba(26, 26, 46, 0.9)"
                    : "rgba(255, 255, 255, 0.9)",
                  backdropFilter: "blur(2px)",
                }}
              >
                <CircularProgress />
              </Box>
            )}
            <TableContainer
              sx={{
                opacity: loadingAppointments ? 0.5 : 1,
                transition: "opacity 0.2s ease",
                backgroundColor: isDarkMode
                  ? "transparent"
                  : commonStyles.tableContainer.backgroundColor,
              }}
            >
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      background: colors.table.header.background,
                      "& .MuiTableCell-head": {
                        color: commonStyles.textPrimary.color,
                        background:
                          getTableHeaderStyles(isDarkMode).cellBackground,
                        fontWeight: 600,
                        borderBottom: `1px solid ${customColors.border.light}`,
                      },
                    }}
                    >
                    {/* Prikaži kolonu "Korisnik" samo ako nije customer */}
                    {(() => {
                      const actualUserType = getUserTypeFromCurrentTenant();
                      const isSuperAdmin = actualUserType === "super_admin";
                      const isTenantAdmin = actualUserType === "tenant_admin";
                      return (isSuperAdmin || isTenantAdmin) && <TableCell>Korisnik</TableCell>;
                    })()}
                    <TableCell>Datum & Vreme</TableCell>
                    {/* Prikaži kolonu "Zaposleni" samo ako nije employee */}
                    {(() => {
                      const actualUserType = getUserTypeFromCurrentTenant();
                      const isEmployee = actualUserType === "employee";
                      return !isEmployee && <TableCell>Zaposleni</TableCell>;
                    })()}
                    <TableCell>Usluge</TableCell>
                    <TableCell>Trajanje</TableCell>
                    <TableCell>Cena</TableCell>
                    <TableCell>Status</TableCell>
                    {/* Prikaži kolonu "Kreirao" samo za super admina */}
                    {(() => {
                      const actualUserType = getUserTypeFromCurrentTenant();
                      const isSuperAdmin = actualUserType === "super_admin";
                      return isSuperAdmin && <TableCell>Kreirao</TableCell>;
                    })()}
                    <TableCell>Akcije</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredAndSortedAppointments.map((appointment, index) => (
                    <TableRow
                      key={appointment._id}
                      sx={{
                        backgroundColor: getActualTableRowBackground(
                          index,
                          isDarkMode
                        ),
                        "&:hover": {
                          background: isDarkMode
                            ? "rgba(75, 85, 99, 0.7)"
                            : "#f5f5f5",
                        },
                        "& .MuiTableCell-body": {
                          borderBottom: `1px solid ${customColors.border.light}`,
                          color: commonStyles.textPrimary.color,
                        },
                      }}
                    >
                      {/* Prikaži kolonu "Klijent" samo ako nije customer */}
                      {(() => {
                        const actualUserType = getUserTypeFromCurrentTenant();
                        const isSuperAdmin = actualUserType === "super_admin";
                        const isTenantAdmin = actualUserType === "tenant_admin";
                        if (isSuperAdmin || isTenantAdmin) {
                          return (
                            <TableCell>
                              <Box
                                sx={{ display: "flex", alignItems: "center", gap: 1 }}
                              >
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: "50%",
                                    background:
                                      appointment.status === "pending"
                                        ? colors.status.appointment.pending
                                        : appointment.status === "confirmed"
                                        ? colors.status.appointment.confirmed
                                        : appointment.status === "cancelled"
                                        ? colors.status.appointment.cancelled
                                        : colors.status.appointment.completed,
                                  }}
                                />
                                {appointment.isQuickAppointment &&
                                appointment.customerName
                                  ? appointment.customerName
                                  : appointment.customerId
                                  ? typeof appointment.customerId === "object"
                                    ? `${appointment.customerId.firstName || ""} ${
                                        appointment.customerId.lastName || ""
                                      }`.trim()
                                    : "N/A"
                                  : "N/A"}
                              </Box>
                            </TableCell>
                          );
                        }
                        return null;
                      })()}
                      <TableCell>
                        {new Date(
                          appointment.appointmentDate
                        ).toLocaleDateString("sr-RS")}
                        {appointment.selectedTime && ` - ${appointment.selectedTime}`}
                      </TableCell>
                      {/* Prikaži kolonu "Zaposleni" samo ako nije employee */}
                      {(() => {
                        const actualUserType = getUserTypeFromCurrentTenant();
                        const isEmployee = actualUserType === "employee";
                        if (!isEmployee) {
                          return (
                            <TableCell>
                              {appointment.employeeId
                                ? typeof appointment.employeeId === "object"
                                  ? `${appointment.employeeId.firstName || ""} ${
                                      appointment.employeeId.lastName || ""
                                    }`.trim() || "N/A"
                                  : "—"
                                : "—"}
                            </TableCell>
                          );
                        }
                        return null;
                      })()}
                      <TableCell>
                        {appointment.serviceIds &&
                        appointment.serviceIds.length > 0
                          ? appointment.serviceIds
                              .map((service) =>
                                typeof service === "object"
                                  ? service.name
                                  : service
                              )
                              .join(", ")
                          : "Nema usluga"}
                      </TableCell>
                      <TableCell>
                        {appointment.duration || appointment.totalDuration || 0}{" "}
                        min
                      </TableCell>
                      <TableCell>
                        <Typography sx={commonStyles.textPrimary}>
                          {appointment.totalPrice ||
                            appointment.calculatedTotalPrice ||
                            "N/A"}{" "}
                          RSD
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const actualUserType = getUserTypeFromCurrentTenant();
                          const isSuperAdmin = actualUserType === "super_admin";
                          const isTenantAdmin = actualUserType === "tenant_admin";
                          
                          if (isSuperAdmin || isTenantAdmin) {
                            return (
                              <FormControl size="small" sx={{ minWidth: 120 }}>
                                <Select
                                  value={appointment.status}
                                  onChange={(e) =>
                                    handleQuickStatusChange(
                                      appointment._id,
                                      e.target.value
                                    )
                                  }
                                  sx={{
                                    fontSize: "0.875rem",
                                    "& .MuiSelect-select": {
                                      display: "flex",
                                      alignItems: "center",
                                    },
                                  }}
                                  renderValue={(value) => {
                                    const iconColor =
                                      value === "pending"
                                        ? colors.status.appointment.pending
                                        : value === "confirmed"
                                        ? colors.status.appointment.confirmed
                                        : colors.status.appointment.cancelled;
                                    
                                    return (
                                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        {value === "pending" && (
                                          <ScheduleIcon
                                            sx={{
                                              fontSize: "1rem",
                                              color: iconColor,
                                              "& path": {
                                                fill: iconColor,
                                              },
                                            }}
                                          />
                                        )}
                                        {value === "confirmed" && (
                                          <CheckCircleIcon
                                            sx={{
                                              fontSize: "1rem",
                                              color: iconColor,
                                              "& path": {
                                                fill: iconColor,
                                              },
                                            }}
                                          />
                                        )}
                                        {value === "cancelled" && (
                                          <CancelIcon
                                            sx={{
                                              fontSize: "1rem",
                                              color: iconColor,
                                              "& path": {
                                                fill: iconColor,
                                              },
                                            }}
                                          />
                                        )}
                                        <Typography>
                                          {value === "pending"
                                            ? "Na čekanju"
                                            : value === "confirmed"
                                            ? "Potvrđeno"
                                            : "Otkazano"}
                                        </Typography>
                                      </Box>
                                    );
                                  }}
                                >
                                  <MenuItem value="pending">
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                      <ScheduleIcon
                                        sx={{
                                          fontSize: "1rem",
                                          color: colors.status.appointment.pending,
                                        }}
                                      />
                                      <Typography>Na čekanju</Typography>
                                    </Box>
                                  </MenuItem>
                                  <MenuItem value="confirmed">
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                      <CheckCircleIcon
                                        sx={{
                                          fontSize: "1rem",
                                          color: colors.status.appointment.confirmed,
                                        }}
                                      />
                                      <Typography>Potvrđeno</Typography>
                                    </Box>
                                  </MenuItem>
                                  <MenuItem value="cancelled">
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                      <CancelIcon
                                        sx={{
                                          fontSize: "1rem",
                                          color: colors.status.appointment.cancelled,
                                        }}
                                      />
                                      <Typography>Otkazano</Typography>
                                    </Box>
                                  </MenuItem>
                                </Select>
                              </FormControl>
                            );
                          } else {
                            return (
                              <Chip
                                icon={
                                  appointment.status === "pending" ? (
                                    <ScheduleIcon sx={{ fontSize: 14 }} />
                                  ) : appointment.status === "confirmed" ? (
                                    <CheckCircleIcon sx={{ fontSize: 14 }} />
                                  ) : appointment.status === "cancelled" ? (
                                    <CancelIcon sx={{ fontSize: 14 }} />
                                  ) : null
                                }
                                label={
                                  appointment.status === "pending"
                                    ? "Na čekanju"
                                    : appointment.status === "confirmed"
                                    ? "Potvrđeno"
                                    : appointment.status === "cancelled"
                                    ? "Otkazano"
                                    : appointment.status === "completed"
                                    ? "Završeno"
                                    : appointment.status
                                }
                                size="small"
                                sx={{
                                  backgroundColor:
                                    appointment.status === "pending"
                                      ? colors.status.appointment.background.pending
                                      : appointment.status === "confirmed"
                                      ? colors.status.appointment.background
                                          .confirmed
                                      : appointment.status === "cancelled"
                                      ? colors.status.appointment.background
                                          .cancelled
                                      : colors.status.appointment.background
                                          .completed,
                                  color:
                                    appointment.status === "pending"
                                      ? colors.status.appointment.pending
                                      : appointment.status === "confirmed"
                                      ? colors.status.appointment.confirmed
                                      : appointment.status === "cancelled"
                                      ? colors.status.appointment.cancelled
                                      : colors.status.appointment.completed,
                                  fontWeight: 600,
                                  height: 28,
                                  borderRadius: 2,
                                  transition: "all 0.2s ease",
                                  "&:hover": {
                                    transform: "translateY(-1px)",
                                  },
                                }}
                              />
                            );
                          }
                        })()}
                      </TableCell>
                      {/* Prikaži kolonu "Kreirao" samo za super admina */}
                      {(() => {
                        const actualUserType = getUserTypeFromCurrentTenant();
                        const isSuperAdmin = actualUserType === "super_admin";
                        if (isSuperAdmin) {
                          return (
                            <TableCell>
                              {appointment.createdBy
                                ? typeof appointment.createdBy === "object"
                                  ? `${appointment.createdBy.firstName || ""} ${
                                      appointment.createdBy.lastName || ""
                                    }`.trim() || appointment.createdBy.email || "N/A"
                                  : "N/A"
                                : "N/A"}
                            </TableCell>
                          );
                        }
                        return null;
                      })()}
                      {(() => {
                        const actualUserType = getUserTypeFromCurrentTenant();
                        const isSuperAdmin = actualUserType === "super_admin";
                        const isTenantAdmin = actualUserType === "tenant_admin";
                        const isCustomer = actualUserType === "customer";
                        
                        if (isSuperAdmin || isTenantAdmin) {
                          return (
                            <TableCell>
                              <Box sx={{ display: "flex", gap: 0.5 }}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleViewClick(appointment)}
                                  sx={{
                                    color: colors.actions.view,
                                    transition: "all 0.2s ease",
                                    "&:hover": {
                                      backgroundColor: `${colors.actions.view}20`,
                                      transform: "scale(1.1)",
                                    },
                                  }}
                                  title="Pregled termina"
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                                {appointment.status === "pending" && (
                                  <IconButton
                                    size="small"
                                    onClick={() => handleOpenDialog(appointment)}
                                    sx={{
                                      color: customColors.primary.main,
                                      transition: "all 0.2s ease",
                                      "&:hover": {
                                        backgroundColor: `${customColors.primary.main}20`,
                                        transform: "scale(1.1)",
                                      },
                                    }}
                                    title="Izmeni termin"
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                )}
                                {appointment.status === "cancelled" && (
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteClick(appointment._id)}
                                    sx={{
                                      color: colors.actions.delete,
                                      transition: "all 0.2s ease",
                                      "&:hover": {
                                        backgroundColor: `${colors.actions.delete}20`,
                                        transform: "scale(1.1)",
                                      },
                                    }}
                                    title="Obriši termin"
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </Box>
                            </TableCell>
                          );
                        }
                        
                        if (isCustomer) {
                          // Proveri da li customer može da otkaže (do dana pre termina)
                          const appointmentDate = new Date(appointment.appointmentDate);
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          appointmentDate.setHours(0, 0, 0, 0);
                          const canCancel = today < appointmentDate;
                          
                          return (
                            <TableCell>
                              <Box sx={{ display: "flex", gap: 0.5 }}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleViewClick(appointment)}
                                  sx={{
                                    color: colors.actions.view,
                                    transition: "all 0.2s ease",
                                    "&:hover": {
                                      backgroundColor: `${colors.actions.view}20`,
                                      transform: "scale(1.1)",
                                    },
                                  }}
                                  title="Pregled termina"
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                                {appointment.status === "pending" && (
                                  <IconButton
                                    size="small"
                                    onClick={() => handleOpenDialog(appointment)}
                                    sx={{
                                      color: customColors.primary.main,
                                      transition: "all 0.2s ease",
                                      "&:hover": {
                                        backgroundColor: `${customColors.primary.main}20`,
                                        transform: "scale(1.1)",
                                      },
                                    }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                )}
                                {appointment.status !== "cancelled" && canCancel && (
                                  <IconButton
                                    size="small"
                                    onClick={() => handleQuickStatusChange(appointment._id, "cancelled")}
                                    sx={{
                                      color: colors.status.appointment.cancelled,
                                      transition: "all 0.2s ease",
                                      "&:hover": {
                                        backgroundColor: `${colors.status.appointment.cancelled}20`,
                                        transform: "scale(1.1)",
                                      },
                                    }}
                                  >
                                    <CancelIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </Box>
                            </TableCell>
                          );
                        }
                        
                        // Za sve ostale tipove korisnika (ako ih ima), samo View dugme
                        return (
                          <TableCell>
                            <Box sx={{ display: "flex", gap: 0.5 }}>
                              <IconButton
                                size="small"
                                onClick={() => handleViewClick(appointment)}
                                sx={{
                                  color: colors.actions.view,
                                  transition: "all 0.2s ease",
                                  "&:hover": {
                                    backgroundColor: `${colors.actions.view}20`,
                                    transform: "scale(1.1)",
                                  },
                                }}
                                title="Pregled termina"
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                        );
                      })()}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Paginacija - samo za desktop */}
            <Pagination
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              showFirstButton
              showLastButton
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="Redova po stranici:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} od ${count !== -1 ? count : `više od ${to}`}`
              }
            />
          </div>
        )
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "200px",
            ...commonStyles.glassContainer,
            borderRadius: 3,
            m: 2,
          }}
        >
          <Typography variant="h6" sx={commonStyles.textSecondary}>
            {(!currentUser?.userType || currentUser?.userType === "customer")
              ? "Nemate zakazane termine"
              : "Nema zakazanih termina"}
          </Typography>
        </Box>
      )}

      {/* Dialog za potvrdu brisanja */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteDialogClose}
        sx={{
          "& .MuiDialog-paper": {
            background: isDarkMode
              ? "rgba(26, 26, 46, 0.95)"
              : "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(10px)",
            borderRadius: 3,
            border: isDarkMode
              ? "1px solid rgba(148, 163, 184, 0.1)"
              : "1px solid rgba(0, 0, 0, 0.1)",
          },
        }}
      >
        <DialogTitle sx={commonStyles.textPrimary}>
          Potvrda brisanja
        </DialogTitle>
        <DialogContent>
          <Typography sx={commonStyles.textSecondary}>
            Da li ste sigurni da želite da obrišete ovaj termin? Ova akcija je trajna i ne može se poništiti.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDeleteDialogClose}
            sx={{
              color: commonStyles.textSecondary.color,
              "&:hover": {
                backgroundColor: isDarkMode
                  ? "rgba(148, 163, 184, 0.1)"
                  : "rgba(0, 0, 0, 0.05)",
              },
            }}
          >
            Otkaži
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            sx={{
              backgroundColor: colors.actions.delete,
              "&:hover": {
                backgroundColor: colors.actions.delete,
                opacity: 0.9,
              },
            }}
          >
            Obriši
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog za pregled termina */}
      <AppointmentViewDialog
        open={viewDialog.open}
        onClose={handleViewDialogClose}
        appointment={viewDialog.appointment}
        services={services}
        tenants={tenants}
        userType={getUserTypeFromCurrentTenant()}
        isDarkMode={isDarkMode}
      />

      {/* Mobilni Filter Modal */}
      <Drawer
        anchor="bottom"
        open={mobileFilterOpen}
        onClose={handleCloseMobileFilter}
        sx={{
          "& .MuiDrawer-paper": {
            height: "85vh",
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            background: isDarkMode
              ? "rgba(26, 26, 46, 0.95)"
              : "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(10px)",
            border: isDarkMode
              ? "1px solid rgba(148, 163, 184, 0.1)"
              : "1px solid rgba(0, 0, 0, 0.1)",
          },
        }}
      >
        <Box
          sx={{
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            gap: 2
          }}
        >
          {/* Header */}
          <Typography
            variant="h6"
            sx={{
              color: isDarkMode ? 'white' : '#1E293B',
              fontWeight: 600
            }}
          >
            Filteri
          </Typography>

          {/* Status */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle1"
              sx={{ ...commonStyles.textPrimary, mb: 2, fontWeight: 600 }}
            >
              Status
            </Typography>
            <FormControl fullWidth>
              <InputLabel sx={{ color: commonStyles.textSecondary.color }}>
                Status
              </InputLabel>
              <Select
                value={tempFilters.statusFilter}
                onChange={(e) =>
                  setTempFilters({ ...tempFilters, statusFilter: e.target.value })
                }
                sx={{
                  color: commonStyles.textPrimary.color,
                  background: getTableHeaderStyles(isDarkMode).cellBackground,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: customColors.border.light,
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: customColors.primary.main,
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: customColors.primary.main,
                  },
                  "& .MuiSelect-icon": {
                    color: commonStyles.textPrimary.color,
                    background: getTableHeaderStyles(isDarkMode).cellBackground,
                  },
                }}
              >
                <MenuItem value="all">Svi</MenuItem>
                <MenuItem value="pending">Na čekanju</MenuItem>
                <MenuItem value="confirmed">Potvrđeno</MenuItem>
                <MenuItem value="cancelled">Otkazano</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Datum od */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle1"
              sx={{ ...commonStyles.textPrimary, mb: 2, fontWeight: 600 }}
            >
              Datum termina
            </Typography>
            <TextField
              fullWidth
              label="Datum od"
              type="date"
              value={tempFilters.dateFrom}
              onChange={(e) =>
                setTempFilters({ ...tempFilters, dateFrom: e.target.value })
              }
              InputLabelProps={{
                shrink: true,
                sx: { color: commonStyles.textSecondary.color },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  color: commonStyles.textPrimary.color,
                  background: getTableHeaderStyles(isDarkMode).cellBackground,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: customColors.border.light,
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: customColors.primary.main,
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: customColors.primary.main,
                  },
                },
              }}
            />
          </Box>

          {/* Datum do */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle1"
              sx={{ ...commonStyles.textPrimary, mb: 2, fontWeight: 600 }}
            >
              Datum do
            </Typography>
            <TextField
              fullWidth
              label="Datum do"
              type="date"
              value={tempFilters.dateTo}
              onChange={(e) =>
                setTempFilters({ ...tempFilters, dateTo: e.target.value })
              }
              InputLabelProps={{
                shrink: true,
                sx: { color: commonStyles.textSecondary.color },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  color: commonStyles.textPrimary.color,
                  background: getTableHeaderStyles(isDarkMode).cellBackground,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: customColors.border.light,
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: customColors.primary.main,
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: customColors.primary.main,
                  },
                },
              }}
            />
          </Box>

          {/* Sortiranje */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle1"
              sx={{ ...commonStyles.textPrimary, mb: 2, fontWeight: 600 }}
            >
              Sortiranje
            </Typography>
            <FormControl fullWidth>
              <InputLabel sx={{ color: commonStyles.textSecondary.color }}>
                Sortiraj po:
              </InputLabel>
              <Select
                value={tempFilters.sortBy}
                onChange={(e) =>
                  setTempFilters({ ...tempFilters, sortBy: e.target.value })
                }
                sx={{
                  color: commonStyles.textPrimary.color,
                  background: getTableHeaderStyles(isDarkMode).cellBackground,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: customColors.border.light,
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: customColors.primary.main,
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: customColors.primary.main,
                  },
                  "& .MuiSelect-icon": {
                    color: commonStyles.textPrimary.color,
                    background:
                      getTableHeaderStyles(isDarkMode).cellBackground,
                  },
                }}
              >
                <MenuItem value="createdAtDesc">Datum kreiranja (najnoviji)</MenuItem>
                <MenuItem value="dateAsc">Datum rastuće</MenuItem>
                <MenuItem value="dateDesc">Datum opadajuće</MenuItem>
                <MenuItem value="priceAsc">Cena rastuće</MenuItem>
                <MenuItem value="priceDesc">Cena opadajuće</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Footer sa dugmićima */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            pt: 2,
            borderTop: `1px solid ${customColors.border.light}`,
            px: 3,
            pb: 3,
          }}
        >
          <Button
            variant="outlined"
            onClick={handleResetMobileFilters}
            sx={{
              flex: 1,
              borderColor: customColors.border.light,
              color: commonStyles.textPrimary.color,
              "&:hover": {
                borderColor: customColors.primary.main,
                backgroundColor: "rgba(59, 130, 246, 0.1)",
              },
            }}
          >
            Resetuj
          </Button>
          <Button
            variant="contained"
            onClick={handleApplyMobileFilters}
            sx={{
              flex: 1,
              ...commonStyles.gradientButton,
            }}
            startIcon={<CheckIcon />}
          >
            Primeni
          </Button>
        </Box>
      </Drawer>
    </div>
  );
};

export default Appointments;
